import styled from 'styled-components';

import { Button } from '@components/button';
import { useFormContext } from '@components/form';
import { useFormField } from '@components/form/use-form-field';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconListDynamic } from '@components/icons/list-dynamic';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { getParameterMapFieldId } from './endpoint-fields';
import type {
  IntegrationEndpointListVariable,
  IntegrationEndpointVariable,
  List,
  SyncListParameter,
} from './integrations-types';
import { AddLookupValueModal, flattenedLookupName } from './lookup-values';
import type { NewLookupValue } from './lookup-values';

type Props = {
  field: IntegrationEndpointListVariable;
  allFields: IntegrationEndpointVariable[];
  arrayFields?: IntegrationEndpointVariable[];
  parameters: SyncListParameter[];
  keepOpen: () => void;
  lists?: List[];
  onCreateLookup?: (newLookupValue: NewLookupValue, beforeName: string) => Promise<string>;
  existingNames?: string[];
};

// Label of the innermost array along a flattened field path, so a nested
// array (`groups__items`) shows its own label rather than the flattened path.
const arrayLabelForPath = (
  fields: IntegrationEndpointVariable[] | undefined,
  [arrayName, ...rest]: string[],
): string => {
  const arrayField = fields?.find((variable) => variable.name === arrayName);
  const label = arrayField?.label || arrayName;
  return rest.length > 0 ? arrayLabelForPath(arrayField?.template, rest) : label;
};

// An array field is offered by its flattened name (`recipients__status`) with an
// array-qualified label, so it stays distinct from a same-named top-level field.
export const buildParameterMapVariableOptions = (
  field: IntegrationEndpointVariable,
  {
    allFields,
    arrayFields = [],
  }: { allFields: IntegrationEndpointVariable[]; arrayFields?: IntegrationEndpointVariable[] },
): { value: string; label: string }[] => {
  const separatorIndex = field.name.lastIndexOf('__');
  const arrayPrefix = separatorIndex === -1 ? '' : field.name.slice(0, separatorIndex);
  const path = arrayPrefix ? arrayPrefix.split('__') : [];
  const arrayLabel = path.length > 0 ? arrayLabelForPath(allFields, path) : '';

  const arrayOptions = arrayFields
    .filter((variable) => variable.type !== 'array')
    .map((variable) => {
      const fieldLabel = variable.label || variable.name;
      return {
        value: arrayPrefix ? `${arrayPrefix}__${variable.name}` : variable.name,
        label: arrayLabel ? `${arrayLabel} · ${fieldLabel}` : fieldLabel,
      };
    });

  const topLevelOptions = allFields
    .filter((variable) => variable.type !== 'array' && variable.name !== field.name)
    .map((variable) => ({ value: variable.name, label: variable.label || variable.name }));

  return [...arrayOptions, ...topLevelOptions];
};

// Registers a form field per parameter, outside the Collapse so closing
// the drawer keeps the mapping.
export const HiddenParameterMapFields = ({
  field,
  parameters,
}: Omit<Props, 'allFields' | 'keepOpen'>) => {
  return (
    <>
      {parameters.map((parameter) => (
        <HiddenParameterField
          key={parameter.name}
          fieldId={getParameterMapFieldId(field.name, parameter.name)}
          defaultValue={field.parameter_map?.[parameter.name] ?? null}
        />
      ))}
    </>
  );
};

export const DynamicListParameterMap = ({
  field,
  allFields,
  arrayFields,
  parameters,
  keepOpen,
  lists,
  onCreateLookup,
  existingNames,
}: Props) => {
  const { t } = useScopedTranslate(locale);
  const { store } = useFormContext();
  const formValues = store.useFormValues();
  const showModal = useModal(ParameterMapModal);
  const showAddLookupValueModal = useModal(AddLookupValueModal);

  if (parameters.length === 0) return null;

  const variableOptions = buildParameterMapVariableOptions(field, { allFields, arrayFields });

  const optionValues = new Set(variableOptions.map((option) => option.value));
  const currentMap: Record<string, string> = {};
  for (const parameter of parameters) {
    const value = formValues[getParameterMapFieldId(field.name, parameter.name)];
    // A mapping to a since-deleted variable doesn't count as mapped.
    if (typeof value === 'string' && value && optionValues.has(value)) {
      currentMap[parameter.name] = value;
    }
  }
  const filledCount = Object.keys(currentMap).length;
  const isComplete = filledCount === parameters.length;

  const handleAddLookup =
    lists && onCreateLookup
      ? (parameterName: string) => {
          // Replaces the parameter-map modal (the modal system shows one at a time).
          showAddLookupValueModal({
            lists,
            isNameTaken: (name) =>
              (existingNames ?? []).includes(flattenedLookupName(field.name, name)),
            onSave: async (newLookupValue) => {
              const lookupName = await onCreateLookup(newLookupValue, field.name);
              store.actions.setValue(getParameterMapFieldId(field.name, parameterName), lookupName);
            },
          });
        }
      : undefined;

  const handleClick = () => {
    // Keep the drawer open while mapping so it doesn't auto-collapse the
    // moment the last parameter is filled (which would unmount the modal trigger).
    keepOpen();
    showModal({
      variableLabel: field.label || field.name,
      parameters,
      variableOptions,
      currentMap,
      onChange: (parameterName, value) => {
        store.actions.setValue(getParameterMapFieldId(field.name, parameterName), value || null);
      },
      onAddLookup: handleAddLookup,
    });
  };

  const color = isComplete ? 'elementPurple100' : 'elementRed100';
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      color={color}
      iconLeft={<IconListDynamic size={16} color={color} />}
      text={t('status', { filled: filledCount, total: parameters.length })}
      // marginLeft lines up with the list input: 24px arrow + 16px row gap.
      style={{ alignSelf: 'flex-start', marginLeft: pxToRem(40), marginTop: pxToRem(8) }}
    />
  );
};

const HiddenParameterField = ({
  fieldId,
  defaultValue,
}: {
  fieldId: string;
  defaultValue: string | null;
}) => {
  useFormField({ id: fieldId, label: '', defaultValue });
  return null;
};

type FieldOption = { value: string; label: string };

type ParameterMapModalProps = {
  variableLabel: string;
  parameters: SyncListParameter[];
  variableOptions: FieldOption[];
  currentMap: Record<string, string>;
  onChange: (parameter: string, value: string) => void;
  onAddLookup?: (parameterName: string) => void;
};

const ParameterMapModal = ({
  variableLabel,
  parameters,
  variableOptions,
  currentMap,
  onChange,
  onAddLookup,
}: ParameterMapModalProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Modal>
      <Modal.SimpleHeader title={t('modalTitle', { variable: variableLabel })} />
      <Modal.Content>
        <Stack space={4}>
          <Text text={t('modalDescription')} font="regular14" color="textSecondary" />
          <Stack space={2}>
            {parameters.map((parameter) => (
              <Stack key={parameter.name} orientation="horizontal" space={3} align="flex-start">
                <ParameterLabel>
                  <Text
                    text={parameter.label}
                    font="regular14"
                    color="textHelper"
                    ellipsis
                    tooltip={parameter.label}
                  />
                </ParameterLabel>
                <ParameterControl>
                  <Stack space={2}>
                    <Select
                      label={parameter.label}
                      hideLabel
                      options={variableOptions}
                      value={currentMap[parameter.name] || undefined}
                      onChange={(value) => onChange(parameter.name, (value as string) ?? '')}
                      placeholder={t('selectField')}
                    />
                    {onAddLookup && (
                      <Stack orientation="horizontal">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft={<IconAddPlus size={16} />}
                          text={t('addLookupValue')}
                          onClick={() => onAddLookup(parameter.name)}
                        />
                      </Stack>
                    )}
                  </Stack>
                </ParameterControl>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('close')} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const ParameterLabel = styled.div({
  maxWidth: pxToRem(140),
  flexShrink: 0,
});

const ParameterControl = styled.div({
  flexGrow: 1,
  minWidth: 0,
});

const locale = {
  en: {
    status: '{filled}/{total} parameters mapped',
    modalTitle: 'Map parameters for {variable}',
    modalDescription:
      "This list's options depend on values from other endpoint variables. Choose which variable provides each parameter so the list can load.",
    selectField: 'Select field',
    addLookupValue: 'Add lookup value',
    close: 'Close',
  },
};
